import React from 'react';

import * as formTypes from 'constants/formTypes';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import { SupervisorModel } from 'forms/sections/history/employment/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { setAttribute } from 'components/Form/shared/setAttribute';
import { i18n } from 'config/locales';
import Field from 'components/Form/Field';
import Show from 'components/Form/Show';
import NotApplicable from 'components/Form/NotApplicable';
import Email from 'components/Form/Email';
import PVQEmail from 'components/Form/PVQEmail';
import Branch from 'components/Form/Branch';
import Telephone from 'components/Form/Telephone';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Textarea from 'components/Form/Textarea';
import Comments from 'components/Form/Comments';
import PVQName from 'components/Form/PVQName';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { isPVQForm } from 'helpers/form';
import {
  ApplicableModel, CountryArrayModel, CountryModel, LocationModel
} from 'forms/shared/types';
import { isSafeInternational } from 'helpers/locations';
import TelephoneCollection from 'components/Form/Telephone/TelephoneCollection';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';

interface Props extends ValidationElementProps, SupervisorModel {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: formTypes.FormTypes
  nonMilitary?: any
  addressBooks?: CommonAddressBooks
  addressBook?: any
  attribute?: string
  showMultipleTelephones?: boolean
  showMultipleEmails?: boolean
  includeEmailType?: boolean

  // TODO: Remove.
  dispatch?: Dispatch
  requireSupervisorHasDifferentAddress?: boolean
  requireSupervisorAddress?: boolean
  requireEmploymentHistorySupervisorEmail?: boolean
}

export default class Supervisor extends ValidationElement<Props> {
  update = (queue: Partial<SupervisorModel>) => {
    this.props.onUpdate({
      SupervisorName: this.props.SupervisorName,
      SupervisorNamePvq: this.props.SupervisorNamePvq,
      SupervisorAssignedUnitOrg: this.props.SupervisorAssignedUnitOrg,
      Title: this.props.Title,
      Email: this.props.Email,
      EmailNotApplicable: this.props.EmailNotApplicable,
      Address: this.props.Address,
      Telephone: this.props.Telephone,
      HasDifferentSupervisorAddress: this.props.HasDifferentSupervisorAddress,
      TitleNotApplicable: this.props.TitleNotApplicable,
      SupervisorAssignedUnitOrgNotApplicable: this.props.SupervisorAssignedUnitOrgNotApplicable,
      AddressNotApplicable: this.props.AddressNotApplicable,
      InUsaNotApplicable: this.props.InUsaNotApplicable,
      Telephones: this.props.Telephones,
      Emails: this.props.Emails,
      ...queue,
    });
  };

  updateSupervisorName = (values: SupervisorModel['SupervisorName']) => {
    this.update({
      SupervisorName: values,
      SupervisorNamePvq: undefined,
    });
  };

  updateSupervisorNamePvq = (values: SupervisorModel['SupervisorNamePvq']) => {
    this.update({
      SupervisorName: undefined,
      SupervisorNamePvq: values,
    });
  };

  updateTitle = (values: SupervisorModel['Title']) => {
    this.update({
      Title: this.props.TitleNotApplicable?.applicable !== false ? values : {} as SupervisorModel['Title'],
    });
  };

  updateSupervisorAssignedUnitOrg = (values: SupervisorModel['SupervisorAssignedUnitOrg']) => {
    this.update({
      SupervisorAssignedUnitOrg: values,
    });
  };

  updateEmailNotApplicable = (values: SupervisorModel['EmailNotApplicable']) => {
    this.update({
      EmailNotApplicable: values,
      Email: values?.applicable !== false ? this.props.Email : {} as SupervisorModel['Email'],
    });
  };

  updateEmail = (values: SupervisorModel['Email']) => {
    this.update({
      Email: values,
    });
  };

  updateAddress = (values: SupervisorModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updateTelephone = (values: SupervisorModel['Telephone']) => {
    this.update({
      Telephone: values,
    });
  };

  updateHasDifferentSupervisorAddress = (
    values: SupervisorModel['HasDifferentSupervisorAddress']
  ) => {
    this.update({
      HasDifferentSupervisorAddress: values,
      Address: values.value === 'No' ? {} as SupervisorModel['Address'] : this.props.Address,
    });
  };

  updateTitleNotApplicable = (values: SupervisorModel['TitleNotApplicable']) => {
    this.update({
      TitleNotApplicable: values,
      Title: values?.applicable !== false ? this.props.Title : {} as SupervisorModel['Title'],
    });
  };

  updateSupervisorAssignedUnitOrgNotApplicable = (values: SupervisorModel['SupervisorAssignedUnitOrgNotApplicable']) => {
    this.update({
      SupervisorAssignedUnitOrgNotApplicable: values,
      SupervisorAssignedUnitOrg: values?.applicable !== false ? this.props.SupervisorAssignedUnitOrg : {} as SupervisorModel['SupervisorAssignedUnitOrg'],
    });
  };

  updateAddressNotApplicable = (
    addressNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<SupervisorModel> = {
      AddressNotApplicable: { applicable: addressNotApplicable.applicable }
    };

    const Address: LocationModel | undefined = this.props.Address;

    let country: CountryModel | string | CountryArrayModel | undefined = Address?.country;

    if (country && !addressNotApplicable.applicable) {
      if (isSafeInternational(Address)) {
        country = { value: 'INTERNATIONAL' } as CountryModel;
      }

      queue.Address = { country } as LocationModel;
    }

    this.update(queue);
  };

  updateInUsaNotApplicable = (
    inUsaNotApplicable: ApplicableModel
  ) => {
    const queue: Partial<SupervisorModel> = {
      InUsaNotApplicable: { applicable: inUsaNotApplicable.applicable }
    };

    if (!inUsaNotApplicable.applicable) {
      queue.Address = {} as LocationModel;
    }

    this.update(queue);
  };

  updateTelephones = (values: SupervisorModel['Telephones']) => {
    this.update({ Telephones: values });
  };

  updateEmails = (values: SupervisorModel['Emails']) => {
    this.update({ Emails: values });
  };

  render() {
    const supervisorTitleLabel = this.props.nonMilitary
      ? i18n.t('history.pvq.employment.default.supervisor.heading.title')
      : i18n.t('history.pvq.employment.default.supervisor.heading.titleMilitary');

    const supervisorAssignedUnitOrgLabel = i18n.t('history.pvq.employment.default.supervisor.heading.assignedUnitOrg');
    const showPvq = isPVQForm(this.props.formType);
    return showPvq ? (
      <div className="supervisor">
        <Field
          attribute={setAttribute(this.props.attribute, 'SupervisorNamePvq')}
          title={i18n.t('history.pvq.employment.default.supervisor.heading.name')}
          titleSize="h4"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQName
            attribute={setAttribute(this.props.attribute, 'SupervisorNamePvq')}
            exactAttribute
            aria-label="Provide full name"
            name="SupervisorNamePvq"
            {...this.props.SupervisorNamePvq}
            onUpdate={this.updateSupervisorNamePvq}
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            hideMiddleName
            showLastFirstMiddleNameOrder
          />
        </Field>
        <Field
          attribute={setAttribute(this.props.attribute, 'Title')}
          title={supervisorTitleLabel}
          titleSize="h4"
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            attribute={setAttribute(this.props.attribute, 'TitleNotApplicable')}
            name="TitleNotApplicable"
            {...this.props.TitleNotApplicable}
            className="supervisor-title-na"
            label={i18n.t('reference.label.idk')}
            or={i18n.m('reference.para.or')}
            onUpdate={this.updateTitleNotApplicable}
            onError={this.props.onError}
          >
            <Text
              attribute={setAttribute(this.props.attribute, 'Title')}
              name="Title"
              {...this.props.Title}
              className="text full-width supervisor-title"
              onUpdate={this.updateTitle}
              onError={this.props.onError}
              required={this.props.required}
            />
          </NotApplicable>
        </Field>
        {this.props.showMultipleTelephones

          ? (
            <TelephoneCollection
              {...this.props.Telephones}
              onUpdate={this.updateTelephones}
              onError={this.props.onError}
              required={this.props.required}
              attribute={setAttribute(this.props.attribute, 'Telephones')}
              telephoneFieldTitle={i18n.t('history.pvq.employment.default.supervisor.heading.telephones')}
              appendFieldTitle={i18n.t('history.pvq.employment.default.supervisor.heading.telephonesAppend')}
              hideInitialBranchQuestion
              allowNotApplicable
              showTimeOfDay
              timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
            />
          ) : (
            <Field
              attribute={setAttribute(this.props.attribute, 'Telephone')}
              title={i18n.t('history.pvq.employment.default.supervisor.heading.telephone')}
              titleSize="h4"
              className="override-required"
              adjustFor="telephone"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Telephone
                attribute={setAttribute(this.props.attribute, 'Telephone')}
                name="Telephone"
                {...this.props.Telephone}
                className="supervisor-telephone"
                onUpdate={this.updateTelephone}
                allowNotApplicable
                showTimeOfDay
                showNumberType
                timeOfDayLabel={i18n.t('identification.pvq.contacts.collection.phoneNumbers.timeOfDayLabel')}
                onError={this.props.onError}
                required={this.props.required}
                formType={this.props.formType}
              />
            </Field>
          )}

        {this.props.showMultipleEmails

          ? (
            <EmailCollection
              {...this.props.Emails}
              onUpdate={this.updateEmails}
              onError={this.props.onError}
              required={this.props.required}
              attribute={setAttribute(this.props.attribute, 'Emails')}
              emailFieldTitle={i18n.t('history.pvq.employment.default.supervisor.heading.emailsEmailFieldTitle')}
              appendFieldTitle={i18n.t('history.pvq.employment.default.supervisor.heading.emailsAppend')}
              branchFieldTitle={i18n.t('history.pvq.employment.default.supervisor.heading.emails')}
              hasSecondaryAppendLabels
              secondaryNoAppendLabel="No"
              secondaryYesAppendLabel="Yes"
              showEmailUnknown
              includeEmailType={this.props.includeEmailType}
            />
          ) : (
            <Field
              attribute={setAttribute(this.props.attribute, 'Email')}
              title={i18n.t('history.pvq.employment.default.supervisor.heading.email')}
              titleSize="h4"
              adjustFor="label"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <NotApplicable
                attribute={setAttribute(this.props.attribute, 'EmailNotApplicable')}
                name="EmailNotApplicable"
                {...this.props.EmailNotApplicable}
                className="supervisor-email-na"
                label={i18n.t('reference.label.idk')}
                or={i18n.m('reference.para.or')}
                onUpdate={this.updateEmailNotApplicable}
                onError={this.props.onError}
              >
                <PVQEmail
                  attribute={setAttribute(this.props.attribute, 'Email')}
                  name="Email"
                  {...this.props.Email}
                  includeEmailType={this.props.includeEmailType}
                  className="text supervisor-email"
                  onUpdate={this.updateEmail}
                  onError={this.props.onError}
                  required={
                    (this.props.EmailNotApplicable || {}).applicable === false
                      ? false
                      : this.props.required
                  }
                />
              </NotApplicable>
            </Field>
          )}
        {!this.props.nonMilitary
          && (
            <Field
              attribute={setAttribute(this.props.attribute, 'SupervisorAssignedUnitOrg')}
              title={supervisorAssignedUnitOrgLabel}
              titleSize="h4"
              adjustFor="labels"
              scrollIntoView={this.props.scrollIntoView}
            >
              <NotApplicable
                attribute={setAttribute(this.props.attribute, 'SupervisorAssignedUnitOrgNotApplicable')}
                name="SupervisorAssignedUnitOrgNotApplicable"
                {...this.props.SupervisorAssignedUnitOrgNotApplicable}
                className="supervisor-asigned-unit-org-na"
                label={i18n.t('reference.label.idk')}
                or={i18n.m('reference.para.or')}
                onUpdate={this.updateSupervisorAssignedUnitOrgNotApplicable}
                onError={this.props.onError}
              >
                <Text
                  attribute={setAttribute(this.props.attribute, 'SupervisorAssignedUnitOrg')}
                  name="SupervisorAssignedUnitOrg"
                  {...this.props.SupervisorAssignedUnitOrg}
                  className="text full-width supervisor-title"
                  onUpdate={this.updateSupervisorAssignedUnitOrg}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
          )}

        <Field
          attribute={setAttribute(this.props.attribute, 'Address')}
          title={i18n.t('history.pvq.employment.default.supervisor.heading.address')}
          titleSize="h4"
          optional
          help="history.pvq.employment.default.supervisor.address.help"
          adjustFor="address"
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQLocationUnknown
            {...this.props.Address}
            addressUnknown={this.props.AddressNotApplicable}
            addressUnknownLabel={i18n.t('reference.label.idk')}
            inUsaUnknown={this.props.InUsaNotApplicable}
            inUsaUnknownLabel={i18n.t('reference.label.idk')}
            attribute={setAttribute(this.props.attribute, 'Address')}
            name="Address"
            className="supervisor-address"
            label={i18n.t(`history.pvq.employment.default.supervisor.address.label`)}
            domesticTitle={i18n.t(`history.pvq.employment.default.supervisor.address.domesticTitle`)}
            foreignTitle={i18n.t(`history.pvq.employment.default.supervisor.address.foreignTitle`)}
            internationalStreetLabel={i18n.t('address.pvq.international.street.label')}
            includeMilitaryStates
            layout={Location.ADDRESS}
            geocode
            addressBook="this.props.addressBook"
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateAddress}
            onUpdateAddressUnknown={this.updateAddressNotApplicable}
            onUpdateInUsaUnknown={this.updateInUsaNotApplicable}
            onError={this.props.onError}
            required={this.props.required}
            isPoBoxAllowed={false}
            showAddressUnknown
            showInUsaUnknown
            showMilitaryInstallation
            showInternationalStreet
            hideInstallationNameUnknownWhenForeign
            showIsInstallationUnknownIfInternational
            showIsInstallationUnknownIfDomestic
            hideInstallationNameUnknownExplanation
            showApoFpoDpoPostcodeWhenForeign
          />
        </Field>
        {/* div below is End of the PVQ version */}
      </div>

    ) : (

      <div className="supervisor">
        <Show when={this.props.requireSupervisorHasDifferentAddress}>
          <Branch
            attribute={setAttribute(this.props.attribute, 'HasDifferentAddress')}
            label={i18n.t('history.employment.default.supervisor.hasdifferentaddress.label')}
            labelSize="h4"
            name="HasDifferentAddress"
            {...this.props.HasDifferentSupervisorAddress}
            className="has-different-address"
            help="history.employment.default.supervisor.hasdifferentaddress.help"
            onUpdate={this.updateHasDifferentSupervisorAddress}
            onError={this.props.onError}
            required={this.props.required && this.props.requireSupervisorHasDifferentAddress}
            scrollIntoView={this.props.scrollIntoView}
            warning
          />
        </Show>
        <Show
          when={
            this.props.HasDifferentSupervisorAddress.value === 'Yes'
            || this.props.requireSupervisorAddress
          }
        >
          <Field
            attribute={setAttribute(this.props.attribute, 'SupervisorName')}
            title={i18n.t('history.employment.default.supervisor.heading.name')}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              attribute={setAttribute(this.props.attribute, 'SupervisorName')}
              name="SupervisorName"
              className="text full-width supervisor-name"
              {...this.props.SupervisorName}
              onError={this.props.onError}
              onUpdate={this.updateSupervisorName}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={setAttribute(this.props.attribute, 'Title')}
            title={supervisorTitleLabel}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Text
              attribute={setAttribute(this.props.attribute, 'Title')}
              name="Title"
              {...this.props.Title}
              className="text full-width supervisor-title"
              onUpdate={this.updateTitle}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
          <Show when={this.props.requireEmploymentHistorySupervisorEmail}>
            <Field
              attribute={setAttribute(this.props.attribute, 'Email')}
              title={i18n.t('history.employment.default.supervisor.heading.email')}
              titleSize="h4"
              adjustFor="label"
              shrink
              scrollIntoView={this.props.scrollIntoView}
            >
              <NotApplicable
                attribute={setAttribute(this.props.attribute, 'EmailNotApplicable')}
                name="EmailNotApplicable"
                {...this.props.EmailNotApplicable}
                className="supervisor-email-na"
                label={i18n.t('reference.label.idk')}
                or={i18n.m('reference.para.or')}
                onUpdate={this.updateEmailNotApplicable}
                onError={this.props.onError}
              >
                <Email
                  attribute={setAttribute(this.props.attribute, 'Email')}
                  name="Email"
                  {...this.props.Email}
                  className="text supervisor-email"
                  onUpdate={this.updateEmail}
                  onError={this.props.onError}
                  required={
                    (this.props.EmailNotApplicable || {}).applicable === false
                      ? false
                      : this.props.required
                  }
                />
              </NotApplicable>
            </Field>
          </Show>
          <Field
            attribute={setAttribute(this.props.attribute, 'Address')}
            title={i18n.t('history.employment.default.supervisor.heading.address')}
            titleSize="h4"
            optional
            help="history.employment.default.supervisor.address.help"
            adjustFor="address"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Location
              attribute={setAttribute(this.props.attribute, 'Address')}
              name="Address"
              {...this.props.Address}
              label={i18n.t('history.employment.default.supervisor.address.label')}
              className="supervisor-address"
              layout={Location.ADDRESS}
              geocode
              addressBooks={this.props.addressBooks}
              addressBook={this.props.addressBook}
              showPostOffice
              dispatch={this.props.dispatch}
              onUpdate={this.updateAddress}
              onError={this.props.onError}
              required={
                this.props.requireSupervisorHasDifferentAddress
                  ? this.props.required && this.props.HasDifferentSupervisorAddress.value === 'Yes'
                  : this.props.required
              }
            />
          </Field>
        </Show>
        {(this.props.HasDifferentSupervisorAddress.value === 'Yes'
          || this.props.requireSupervisorAddress) && (
            <Field
              attribute={setAttribute(this.props.attribute, 'Telephone')}
              title={i18n.t('history.employment.default.supervisor.heading.telephone')}
              titleSize="h4"
              className="override-required"
              adjustFor="telephone"
              scrollIntoView={this.props.scrollIntoView}
            >
              <Telephone
                attribute={setAttribute(this.props.attribute, 'Telephone')}
                name="Telephone"
                {...this.props.Telephone}
                className="supervisor-telephone"
                onUpdate={this.updateTelephone}
                allowNotApplicable={false}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
        )}
        <Comments
          title={i18n.t('history.employment.label.supervisorcomments')}
          name="Supervisor Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.props.updateSupervisorComments}
          required={false}
        />
      </div>
    );
  }
}

Supervisor.defaultProps = {
  SupervisorName: {},
  SupervisorNamePvq: {},
  SupervisorAssignedUnitOrg: {},
  Title: {},
  Email: {},
  EmailNotApplicable: { applicable: true },
  HasDifferentSupervisorAddress: {},
  TitleNotApplicable: { applicable: true },
  SupervisorAssignedUnitOrgNotApplicable: { applicable: true },
  AddressNotApplicable: { applicable: true },
  InUsaNotApplicable: { applicable: true },
  Telephones: {},
  Emails: {},
  Comments: {},
  Address: {},
  Telephone: {},
  addressBooks: {},
  addressBook: 'Employment',
  showMultipleTelephones: false,
  showMultipleEmails: false,
  dispatch: () => { },
  onUpdate: () => { },
  updateSUpervisorComments: () => { },
  onError: (value, arr) => arr,
};
